import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Divider,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import type { RuteroVertical, VoucherTextLanguage, VoucherTextMode, VoucherTextScope } from '@bandido/shared/types';

import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { IdentitySection } from '@frontend/features/vouchers/components/IdentitySection';
import { useRuteroConnectors } from '@frontend/features/rutero/queries';
import { PreviewPanel } from '@frontend/features/vouchers/components/PreviewPanel';
import { ProvidersSection } from '@frontend/features/vouchers/components/ProvidersSection';
import { TextsSection } from '@frontend/features/vouchers/components/TextsSection';
import { VerticalsSection } from '@frontend/features/vouchers/components/VerticalsSection';
import {
  useSaveVoucherConfig,
  useUploadVoucherLogo,
  useVoucherConfig,
} from '@frontend/features/vouchers/queries';
import {
  VoucherIdentityDraft,
  voucherIdentityChanged,
  voucherIdentityDraft,
  voucherIdentityIssues,
  voucherIdentityPayload,
  voucherLogoError,
  voucherDraftSync,
  voucherSaveDisabledReason,
} from '@frontend/features/vouchers/voucherIdentity';
import {
  VOUCHER_NO_REJECTION,
  VoucherTextsDraft,
  voucherIssuesElsewhere,
  voucherRejectionElsewhere,
  voucherRejectionEmpty,
  voucherSaveFailureKey,
  voucherConnectorModePatch,
  voucherTextsChanged,
  voucherTextId,
  voucherTextsForget,
  voucherTextsRejections,
  voucherTextsDraft,
  voucherTextsIssues,
  voucherTextsPayload,
  voucherTextsServerIssues,
  voucherTextsSync,
} from '@frontend/features/vouchers/voucherTexts';
import { UnsavedChangesPrompt } from '@frontend/features/vouchers/components/UnsavedChangesPrompt';
import { useUnsavedChanges } from '@frontend/features/vouchers/useUnsavedChanges';
import { useVoucherPreview } from '@frontend/features/vouchers/useVoucherPreview';
import { voucherIdentityStamp } from '@frontend/features/vouchers/voucherPreview';

const KEY = 'vouchers.config';
/** Las cuatro anclas de la sección. */
const ANCHORS = ['identity', 'texts', 'byVertical', 'byProvider'] as const;

/**
 * LA SECCIÓN VOUCHERS de la agencia: Identidad, Textos, Por tipo de producto y
 * Por proveedor, con la vista previa al lado.
 *
 * Todas las anclas comparten UN botón y UN cuerpo, y todas mandan solo lo que
 * este asesor cambió.
 */
export const VouchersView = () => {
  const { id } = useParams();
  const agencyId = Number(id);
  const theme = useTheme();
  const { t } = useTranslation();
  const { ability } = useAuth();
  const { addToast } = useSnackBar();

  // El mismo permiso que gatea el grupo del menú y que el `PUT` exige.
  const canEdit = ability?.can('create', 'CasePublicFilters') ?? false;

  const config = useVoucherConfig(agencyId);
  // Los proveedores salen del hub, por agencia: no hay catálogo local.
  const connectors = useRuteroConnectors(agencyId);
  const save = useSaveVoucherConfig(agencyId);
  const uploadLogo = useUploadVoucherLogo(agencyId);

  // La agencia que se está viendo AHORA, para que una respuesta que vuelve
  // tarde pueda preguntar contra el presente y no contra el render que la pidió.
  const agenciaActual = useRef(agencyId);
  agenciaActual.current = agencyId;

  const [anchor, setAnchor] = useState<(typeof ANCHORS)[number]>('identity');
  const [draft, setDraft] = useState<VoucherIdentityDraft>(() =>
    voucherIdentityDraft(undefined),);
  const [texts, setTexts] = useState<VoucherTextsDraft>(() =>
    voucherTextsDraft(undefined),);
  /*
   * Lo que rechazó el backend, por bloque y POR FUENTE. Viven aparte de lo que
   * la pantalla detecta —el backend manda sobre qué se puede guardar— y aparte
   * entre sí: un dibujo exitoso de la vista previa borraba el rechazo del
   * guardado, y justo el que importa (el que el validador compartido no
   * reproduce, que la preview nunca va a repetir).
   */
  const [rechazo, setRechazo] = useState(VOUCHER_NO_REJECTION);
  const [rechazoPreview, setRechazoPreview] = useState(VOUCHER_NO_REJECTION);
  /*
   * Qué ejemplo dibuja la vista previa. El vertical y el idioma son de la
   * PREVIEW y no del formulario: se mira el documento de transfers mientras se
   * escribe la bienvenida, y se mira en inglés lo que se acaba de traducir.
   */
  const [previewVertical, setPreviewVertical] = useState<RuteroVertical>('transfers');
  /** El tipo de producto que se está editando, si hay alguno abierto. */
  const [verticalAbierto, setVerticalAbierto] = useState<string | undefined>(undefined);
  /**
   * El proveedor de la fila abierta: la vista previa dibuja con él, porque su
   * texto solo se imprime en SUS reservas.
   */
  const [previewConector, setPreviewConector] = useState<
    { key: string; connector: string; provider: string } | undefined
  >(undefined);
  const [previewLang, setPreviewLang] = useState<VoucherTextLanguage>('es');
  const [logoError, setLogoError] = useState<
    | { message?: string; key: string; params?: Record<string, number> }
    | undefined
  >(undefined);

  const incoming = useMemo(
    () => voucherIdentityDraft(config.data?.identity),
    [config.data?.identity],
  );
  const incomingTexts = useMemo(
    () => voucherTextsDraft(config.data?.blocks),
    [config.data?.blocks],
  );

  /*
   * El borrador se mide contra su BASE —de dónde salió— y la base está atada a
   * la AGENCIA que se leyó. Las dos cosas importan:
   *
   * - Contra la base y no contra lo último del servidor: si otro
   *   administrador cambia la identidad y react-query refresca al volver a la
   *   pestaña, un borrador que nadie tocó tiene que adoptar lo nuevo, no
   *   pasar a figurar como "cambios sin guardar" y revertírselo al otro.
   * - Atada a la agencia: la ruta es la misma para todas
   *   (`/agencias/:id/vouchers`), así que cambiar el id NO desmonta la
   *   pantalla, y sin esto el formulario seguía mostrando —y podía guardar—
   *   el RUT de la agencia anterior sobre la nueva.
   */
  const [base, setBase] = useState<{
    agencyId: number;
    identity: VoucherIdentityDraft;
    texts: VoucherTextsDraft;
  } | null>(null);

  useEffect(() => {
    if (base && base.agencyId !== agencyId) {
      setBase(null);
      setDraft(voucherIdentityDraft(undefined));
      setTexts(voucherTextsDraft(undefined));
      setLogoError(undefined);
      setRechazo(VOUCHER_NO_REJECTION);
      setRechazoPreview(VOUCHER_NO_REJECTION);
      // El proveedor de la vista previa era de la otra agencia: nadie lo eligió acá.
      setPreviewConector(undefined);
    }
  }, [agencyId, base]);

  useEffect(() => {
    if (!config.data) return;
    if (!base || base.agencyId !== agencyId) {
      setDraft(incoming);
      setTexts(incomingTexts);
      setBase({ agencyId, identity: incoming, texts: incomingTexts });
      return;
    }
    // Mientras un guardado viaja, lo que llegue no toca el borrador: la
    // respuesta del propio guardado es la que manda, y pisar acá borraría lo
    // que el asesor siguió escribiendo.
    if (save.isLoading) return;
    // Las dos mitades se sincronizan por separado y con la misma regla: la que
    // nadie tocó adopta lo nuevo. Juntas, escribir un texto congelaba la
    // identidad en lo que se había leído al abrir.
    const sync = voucherDraftSync({
      draft,
      base: base.identity,
      incoming,
    });
    const syncTexts = voucherTextsSync({
      draft: texts,
      base: base.texts,
      incoming: incomingTexts,
    });
    if (!sync && !syncTexts) return;
    if (sync) setDraft(sync.draft);
    if (syncTexts) setTexts(syncTexts.draft);
    setBase({
      agencyId,
      identity: sync ? sync.base : base.identity,
      texts: syncTexts ? syncTexts.base : base.texts,
    });
  }, [
    config.data,
    incoming,
    incomingTexts,
    base,
    agencyId,
    draft,
    texts,
    save.isLoading,
  ]);

  const loaded = base?.agencyId === agencyId;
  const issues = voucherIdentityIssues(draft);
  // Contra los topes que PUBLICA el `GET`, que son los que rechaza el borde.
  const textIssues = voucherTextsIssues(texts, config.data?.limits);
  const changed = Boolean(
    loaded &&
      base &&
      (voucherIdentityChanged(draft, base.identity) ||
        voucherTextsChanged(texts, base.texts)),
  );
  const disabledReason = voucherSaveDisabledReason({
    loaded: Boolean(loaded),
    canEdit,
    changed,
    saving: save.isLoading,
    issues,
    // Solo lo que ESTA pantalla detecta apaga el Guardar. Un rechazo del
    // backend que el validador compartido no reproduce se muestra, pero no
    // puede dejar la pantalla sin forma de reintentar.
    textIssues,
  });
  /*
   * La vista previa dibuja con el BORRADOR, así que se pide sola mientras se
   * escribe. Su 400 se muestra en la misma fila que el del Guardar: es el
   * mismo gate de texto en el backend, así que sería raro que dijeran cosas
   * distintas.
   */
  const preview = useVoucherPreview({
    agencyId,
    loaded: Boolean(loaded),
    texts,
    issues: textIssues,
    vertical: previewVertical,
    provider: previewConector?.provider,
    lang: previewLang,
    // La preview dibuja con la identidad GUARDADA: subir un logo no cambia
    // ningún texto, pero sí lo que se ve.
    identityStamp: voucherIdentityStamp(config.data?.identity),
    onServerIssues: (rechazado) => {
      // Sin rechazos ni antes ni ahora no se toca el estado: cada cambio vuelve
      // a renderizar, y esto llega con CADA respuesta de la vista previa.
      setRechazoPreview((current) =>
        voucherRejectionEmpty(rechazado) && voucherRejectionEmpty(current) ? current : rechazado,);
    },
  });

  /*
   * La pantalla muestra un ancla por vez, así que un problema en la OTRA
   * apagaba el Guardar con un "revisá los campos marcados" y nada marcado a la
   * vista. Se dice dónde está.
   */
  const problemaEnOtraAncla = voucherIssuesElsewhere({
    anchor,
    identityIssues: issues,
    textIssues,
  });

  // Lo que se muestra en las filas: el guardado, la vista previa y lo local.
  const rechazos = voucherTextsRejections({
    save: rechazo,
    preview: rechazoPreview,
    local: textIssues,
  });

  /**
   * Escribir en CUALQUIER caja de texto, de cualquier alcance. Editar una caja
   * invalida además lo que el backend hubiera dicho de ella —del guardado o de
   * la vista previa—: es de un texto que ya no existe.
   */
  const onChangeTexto = (
    scope: VoucherTextScope,
    key: string,
    lang: VoucherTextLanguage,
    body: string,
  ) => {
    const id = voucherTextId(scope, key, lang);
    setTexts((current) => ({ ...current, [id]: body }));
    setRechazo((current) => voucherTextsForget(current, id));
    setRechazoPreview((current) => voucherTextsForget(current, id));
  };

  /** El modo de un proveedor: elegirlo lo fija en sus dos idiomas. */
  const onModo = (key: string, mode: VoucherTextMode) => {
    setTexts((current) => ({ ...current, ...voucherConnectorModePatch(key, mode) }));
  };

  /*
   * Lo que el BACKEND rechazó en otra ancla —guardar es uno solo—. Solo si no
   * hay ya un problema local que señalar: ese apaga Guardar y va primero.
   */
  const rechazoEnOtraAncla = problemaEnOtraAncla
    ? null
    : voucherRejectionElsewhere(anchor, voucherTextsRejections({ save: rechazo, preview: rechazoPreview, local: {} }));

  const unsaved = useUnsavedChanges(changed);

  const onSave = () => {
    const pedidaPara = agencyId;
    /*
     * Contra la BASE, en las dos mitades: solo viaja lo que este asesor
     * cambió. El `PUT` toca únicamente los bloques que el cuerpo nombra, así
     * que sin cambios de texto la clave `blocks` no va —y los textos quedan
     * intactos—, y con cambios viajan solo esos: mandar los cuatro siempre le
     * revertiría al otro administrador el pie que acaba de cambiar.
     */
    const blocks = voucherTextsPayload(texts, base?.texts ?? texts);
    setRechazo(VOUCHER_NO_REJECTION);
    const cuerpo = {
      ...voucherIdentityPayload(draft, base?.identity ?? draft),
      ...(blocks ? { blocks } : {}),
    };
    save.mutate(cuerpo, {
      /*
       * El borrador y su base se rehacen con lo que devolvió el servidor:
       * guarda los valores recortados, y sin esto un espacio al final dejaba
       * la pantalla "con cambios" para siempre.
       *
       * Y solo si la respuesta sigue siendo de la agencia que se está VIENDO
       * —`agenciaActual`, no el valor que se capturó al pedir—: si mientras el
       * PUT viajaba se cambió de agencia por la URL, sembrarlo pondría la
       * identidad de una sobre la otra. Con dos valores capturados en el mismo
       * render la comparación era siempre cierta y no protegía de nada.
       */
      onSuccess: (guardado) => {
        if (pedidaPara === agenciaActual.current) {
          const identity = voucherIdentityDraft(guardado.identity);
          const guardados = voucherTextsDraft(guardado.blocks);
          setDraft(identity);
          setTexts(guardados);
          setBase({ agencyId: pedidaPara, identity, texts: guardados });
        }
        addToast({ message: t(`${KEY}.save.done`), color: 'success' });
      },
      /*
       * El `PUT` es una transacción: si falla, NO quedó nada guardado —ni la
       * identidad ni un texto—, y eso es lo que dice el mensaje. Los bloques
       * rechazados se muestran en su fila; lo que no cae en ninguna fila
       * prende el cartel general en vez de perderse.
       */
      /*
       * Con el MISMO guardo que `onSuccess`: si mientras el `PUT` viajaba se
       * cambió de agencia, el rechazo es de otra y marcaría como problemático
       * un texto de esta que está perfecto.
       */
      onError: (error) => {
        if (pedidaPara !== agenciaActual.current) return;
        // Las cajas de proveedor se buscan en el borrador que se mandó.
        const leido = voucherTextsServerIssues(error, texts);
        setRechazo(leido);
        // El hub dijo que un proveedor ya no está configurado: la lista se
        // relee para que la fila lo diga también fuera del rechazo.
        if (leido.sinConfigurar.length > 0) connectors.refetch();
        addToast({
          message: t(voucherSaveFailureKey(error, leido)),
          color: 'error',
        });
      },
    });
  };

  const onPickLogo = (kind: 'primary' | 'negative', file: File) => {
    setLogoError(undefined);
    uploadLogo.mutate(
      { kind, file },
      {
        onSuccess: () =>
          addToast({ message: t(`${KEY}.logo.done`), color: 'success' }),
        // El estado local NO se toca: el archivo rechazado no reemplaza nada,
        // y el mensaje que se muestra es el del backend.
        onError: (error) => setLogoError(voucherLogoError(error)),
      },
    );
  };

  return (
    <Box
      sx={{
        borderRadius: '24px',
        backgroundColor: theme.palette.common.white,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch',
        height: 'calc(100vh - 180px)',
        overflow: 'hidden',
        padding: theme.spacing(1, 3),
      }}
    >
      <OverlayScrollbarsComponent
        style={{
          marginTop: theme.spacing(4),
          boxSizing: 'border-box',
          width: '100%',
          height: 'calc(100vh - 200px)',
        }}
        className="overlayscrollbars-react"
        options={{ scrollbars: { autoHide: 'leave', theme: 'os-theme-light' } }}
        defer
      >
        <Box display="flex" flexDirection="column" gap={3}>
          <Box>
            <Typography variant="h5">{t(`${KEY}.title`)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t(`${KEY}.intro`)}
            </Typography>
          </Box>

          <Tabs
            value={anchor}
            onChange={(_event, value) => setAnchor(value)}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            {ANCHORS.map((name) => (
              <Tab key={name} value={name} label={t(`${KEY}.anchors.${name}`)} />
            ))}
          </Tabs>
          <Divider />

          {config.isError && <Alert severity="error">{t(`${KEY}.loadFailed`)}</Alert>}
          {problemaEnOtraAncla && (
            <Alert severity="warning">
              {t(`${KEY}.issuesIn.${problemaEnOtraAncla}`)}
            </Alert>
          )}
          {rechazoEnOtraAncla && (
            <Alert severity="error">{t(`${KEY}.rejectedIn.${rechazoEnOtraAncla}`)}</Alert>
          )}

          <Box
            display="grid"
            gap={3}
            gridTemplateColumns={{ xs: '1fr', lg: '2fr 1fr' }}
          >
            <Box>
              {anchor === 'identity' && (
                <IdentitySection
                  draft={draft}
                  issues={issues}
                  logo={config.data?.identity?.logo}
                  logoNegative={config.data?.identity?.logoNegative}
                  /*
                   * Tampoco se edita MIENTRAS se guarda: la respuesta rehace
                   * el borrador con lo que el servidor guardó, así que lo que
                   * se escribiera en el medio desaparecía sin aviso, con un
                   * toast verde y la pantalla "sin cambios".
                   */
                  canEdit={Boolean(canEdit && loaded) && !save.isLoading}
                  savedHeaderColor={base?.identity.headerColor ?? ''}
                  // El MISMO motivo que muestra el botón: sin permiso o sin
                  // configuración leída, pero nunca los dos confundidos.
                  disabledReason={disabledReason ?? undefined}
                  logoError={logoError}
                  onChange={(field, value) =>
                    setDraft((current) => ({ ...current, [field]: value }))
                  }
                  onPickLogo={onPickLogo}
                />
              )}
              {anchor === 'texts' && (
                <TextsSection
                  draft={texts}
                  limits={config.data?.limits}
                  /*
                   * Las tres fuentes juntas, con lo local arriba: si el asesor
                   * ya rompió esa caja, el problema que se muestra tiene que
                   * ser el de AHORA.
                   */
                  issues={rechazos.blocks}
                  otrosRechazados={rechazos.otros}
                  canEdit={Boolean(canEdit && loaded) && !save.isLoading}
                  disabledReason={disabledReason ?? undefined}
                  onChange={onChangeTexto}
                />
              )}
              {anchor === 'byVertical' && (
                <VerticalsSection
                  draft={texts}
                  limits={config.data?.limits}
                  issues={rechazos.blocks}
                  otrosRechazados={rechazos.otros}
                  canEdit={Boolean(canEdit && loaded) && !save.isLoading}
                  disabledReason={disabledReason ?? undefined}
                  open={verticalAbierto}
                  /*
                   * Abrir un producto MUEVE la vista previa a ese producto: el
                   * texto se escribe mirando el documento donde va a salir, que
                   * es lo que hace que se entienda en su lugar.
                   */
                  onOpen={(vertical) => {
                    setVerticalAbierto(vertical);
                    if (vertical) {
                      setPreviewVertical(vertical as RuteroVertical);
                      setPreviewConector(undefined);
                    }
                  }}
                  onChange={onChangeTexto}
                />
              )}
              {anchor === 'byProvider' && (
                <ProvidersSection
                  connectors={connectors.data}
                  connectorsLoading={connectors.isLoading}
                  connectorsFailed={connectors.isError}
                  onRetryConnectors={() => connectors.refetch()}
                  draft={texts}
                  saved={base?.texts ?? texts}
                  limits={config.data?.limits}
                  issues={rechazos.blocks}
                  sinConfigurar={rechazos.sinConfigurar}
                  otrosRechazados={rechazos.otros}
                  canEdit={Boolean(canEdit && loaded) && !save.isLoading}
                  disabledReason={disabledReason ?? undefined}
                  open={previewConector?.key}
                  // Abrir un proveedor mueve la vista previa a SU tipo de
                  // producto y a él: "Ver como {vertical} · {conector}".
                  onOpen={(row) => {
                    setPreviewConector(row);
                    // Una clave vieja sin tipo de producto no mueve el vertical.
                    if (row?.vertical) setPreviewVertical(row.vertical as RuteroVertical);
                  }}
                  onChange={onChangeTexto}
                  onMode={onModo}
                />
              )}
            </Box>

            <PreviewPanel
              vertical={previewVertical}
              connector={previewConector?.connector}
              onClearConnector={() => setPreviewConector(undefined)}
              lang={previewLang}
              url={preview.url}
              loading={preview.loading}
              messageKey={preview.messageKey}
              alternativeKey={preview.alternativeKey}
              onOpen={preview.open}
              onRetry={preview.retry}
              // Otro tipo de producto es otro ejemplo: el proveedor elegido no es suyo.
              onVertical={(vertical) => {
                setPreviewVertical(vertical);
                setPreviewConector(undefined);
              }}
              onLang={setPreviewLang}
            />
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={2} pb={3}>
            <Tooltip title={disabledReason ? t(disabledReason) : ''}>
              <span>
                <LoadingButton
                  variant="contained"
                  loading={save.isLoading}
                  disabled={Boolean(disabledReason)}
                  onClick={onSave}
                >
                  {t(`${KEY}.save.action`)}
                </LoadingButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </OverlayScrollbarsComponent>

      <UnsavedChangesPrompt
        open={unsaved.open}
        onLeave={unsaved.leave}
        onStay={unsaved.stay}
      />
    </Box>
  );
};

export default VouchersView;
